import * as Sentry from '@sentry/react'

import { version } from '../../package.json'
import { Consts } from './consts'

const sensitiveKeys = { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] }

const urlFields = ['from', 'to', 'url'] as const

// Query strings carry email and, on the change-password link, a temporary password.
// dataCollection never reaches the event's page URL or a breadcrumb's.
const withoutQuery = (url: unknown) => (typeof url === 'string' ? url.split(/[?#]/)[0] : url)

export function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  // The message keeps the text; `arguments` is the logged objects themselves.
  if (breadcrumb.category === 'console') return { ...breadcrumb, data: undefined }
  if (!breadcrumb.data) return breadcrumb

  const data = { ...breadcrumb.data }
  for (const field of urlFields) {
    if (field in data) data[field] = withoutQuery(data[field])
  }
  return { ...breadcrumb, data }
}

export function scrubEvent<T extends Sentry.Event>(event: T): T {
  if (!event.request?.url) return event
  return { ...event, request: { ...event.request, url: withoutQuery(event.request.url) as string } }
}

export function setupSentry() {
  const config: Sentry.BrowserOptions = {
    dsn: 'https://8d5de5d7727cb341bbb33314e4fed299@o4508337499865088.ingest.us.sentry.io/4508337642733568',
    debug: Consts.isDev,
    release: version,
    environment: window.location.hostname.includes('localhost') ? 'localhost' : window.location.hostname.split('.')[0],
    enabled: Consts.isProd,
    // v11 collects user info, cookies and request bodies unless told otherwise. These are the
    // v10 defaults, which is what the portal has always sent.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: sensitiveKeys, response: sensitiveKeys },
      httpBodies: [],
      urlQueryParams: sensitiveKeys,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
      frameContextLines: 7,
    },
    attachStacktrace: false,
    integrations: [Sentry.browserSessionIntegration({ lifecycle: 'route' })],
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
  }

  if (config.debug) {
    console.info('Sentry config:', config)
  }

  Sentry.init(config)
}
